'use client';

import { useState, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Cookies from 'js-cookie';
import EditEmailModal from '@/components/comix/EditEmailModal';

// Inner component that uses the hooks (will be wrapped in Suspense by parent)
function SelectEmailBarContent() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [emailLoaded, setEmailLoaded] = useState(false);
  const [editEmailOpen, setEditEmailOpen] = useState(false);

  useEffect(() => {
    const urlSessionId = searchParams.get('sessionId');
    const storedSessionId = Cookies.get('comixSessionId');
    const active = urlSessionId || storedSessionId || null;
    setSessionId(active);
  }, [searchParams]);

  // Refetch whenever the user lands back on /comix/select (not just once per
  // sessionId) — this component is mounted once in comix/layout.tsx and persists
  // across client-side navigation between /comix/* pages, so without `pathname` in
  // the deps, a stale `userEmail` fetched before the email was saved (e.g. on first
  // landing on /comix/create) would stick around forever, making the bar wrongly
  // render nothing when the user later navigates back to /comix/select after saving
  // their email — only a hard refresh (which remounts the layout) fixed it.
  useEffect(() => {
    if (!sessionId || pathname !== '/comix/select') return;
    setEmailLoaded(false);
    fetch(`/api/comix/session?sessionId=${sessionId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        setUserEmail(data?.userEmail ?? null);
        setEmailLoaded(true);
      })
      .catch(() => setEmailLoaded(true));
  }, [sessionId, pathname]);

  if (pathname !== '/comix/select') return null;
  // Only render once we know there's an email to show. Rendering the bar (with a skeleton) while
  // still loading meant first-time users — who have no email yet — saw the skeleton flash in and
  // then vanish when the fetch resolved to `null`, which reads as a bug. Waiting for `emailLoaded`
  // means the bar simply appears when there's something to show, and never flashes for new users.
  if (!emailLoaded || !userEmail) return null;

  return (
    <>
      <div className="bg-white border-t border-gray-200">
        <div className="py-3 md:py-[14px]">
          <div className="container mx-auto max-w-2xl md:max-w-[740px] flex items-center justify-between gap-2 md:gap-[9px] px-4 md:px-[18px]">
            <div className="flex items-center gap-2 md:gap-[9px] min-w-0">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/images/comix/select/mail.svg" alt="" width={18} height={18} className="flex-shrink-0 md:w-5 md:h-5" />
              <span className="text-[14px] md:text-[16px] font-medium text-[#333] truncate">{userEmail}</span>
            </div>
            <button
              id="btn-edit-email"
              type="button"
              onClick={() => setEditEmailOpen(true)}
              className="text-[14px] md:text-[16px] font-normal text-[#333] underline underline-offset-2 flex-shrink-0"
            >
              Edit
            </button>
          </div>
        </div>
      </div>

      <EditEmailModal
        open={editEmailOpen}
        sessionId={sessionId}
        initialEmail={userEmail ?? ''}
        onClose={() => setEditEmailOpen(false)}
        onSaved={(email) => { setUserEmail(email); setEditEmailOpen(false); }}
      />
    </>
  );
}

// Outer component that wraps the content
export default function SelectEmailBar() {
  return <SelectEmailBarContent />;
}
